// ExploitsModal.jsx
import React, { useMemo, useState } from 'react';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Paper,
  Button,
  Chip,
  Stack,
  Tooltip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';


// --- helpers -----------------------------------------------------

const sevColor = {
  critical:'#800080', high:'#f44336', medium:'#ff9800', low:'#2196f3', info:'#4caf50'
};
const methodColor = {
  GET:'#90caf9', POST:'#a5d6a7', PUT:'#ffe082', DELETE:'#ffab91', PATCH:'#ce93d8'
};

const copy = async (text) => {
  try { await navigator.clipboard.writeText(text || ''); } catch {}
};

const parseLocation = (loc) => {
  if (!loc) return { path: '—', method: '' };
  const parts = String(loc).split(' - ');
  if (parts.length === 2) return { path: parts[0], method: parts[1].toUpperCase() };
  const m = String(loc).match(/(.*)\s+-\s+([A-Z]+)$/i);
  if (m) return { path: m[1], method: m[2].toUpperCase() };
  return { path: String(loc), method: '' };
};

const stripAnsi = (str = '') =>
  String(str).replace(/\x1b\[[0-9;]*[mGKHF]/g, '');

// try to pretty print JSON output; returns {text, lang}
const normalizeOutput = (raw) => {
  const clean = stripAnsi(raw ?? '');
  // quick JSON sniff
  try {
    const parsed = JSON.parse(clean);
    return { text: JSON.stringify(parsed, null, 2), lang: 'json' };
  } catch (_) {}
  // sometimes a curl prints headers + JSON; extract trailing JSON
  const maybeJson = clean.match(/\{[\s\S]*\}|\[[\s\S]*\]/);
  if (maybeJson) {
    try {
      const parsed = JSON.parse(maybeJson[0]);
      return { text: JSON.stringify(parsed, null, 2), lang: 'json' };
    } catch (_) {}
  }
  return { text: clean || 'No output available', lang: 'bash' };
};

const isCurl = (cmd = '') => /^\s*(curl)\b/i.test(cmd);

// --- component ---------------------------------------------------

const ExploitsModal = ({ open, handleClose, exploit }) => {
  // report modal (kept from your version, with nicer styling)
  const [reportOpen, setReportOpen] = useState(false);

  const { path, method } = useMemo(
    () => parseLocation(exploit?.impacted_assets),
    [exploit]
  );

  const out = useMemo(
    () => normalizeOutput(exploit?.command_output),
    [exploit?.command_output]
  );

  const commandLang = isCurl(exploit?.command) ? 'bash' : 'bash';

  return (
    <>
      {/* Main Modal (Details) */}
      <Modal open={open} onClose={handleClose} aria-labelledby="modal-title" aria-describedby="modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '94%', md: '80%' },
            maxHeight: '82%',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2.5,
            p: { xs: 2, md: 3 },
          }}
        >
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                size="small"
                label={(exploit?.severity || 'SEV').toUpperCase()}
                sx={{ bgcolor: sevColor[(exploit?.severity || '').toLowerCase()] || '#9e9e9e', color:'#fff', fontWeight: 700 }}
              />
              <Typography id="modal-title" variant="h6" sx={{ fontWeight: 800 }}>
                {exploit?.name || 'Exploit Details'}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" onClick={() => setReportOpen(true)}>
                Report
              </Button>
              <IconButton onClick={handleClose} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {!exploit ? (
            <Typography>No exploit details available.</Typography>
          ) : (
            <Stack spacing={2.5} id="modal-description">
              {/* Overview */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Overview
                </Typography>
                <Stack spacing={1} useFlexGap flexWrap="wrap" direction="row">
                  <Chip size="small" variant="outlined" label={exploit?.created_at || '—'} />
                  {!!method && (
                    <Chip size="small" label={method} sx={{ bgcolor: methodColor[method] || '#e0e0e0' }} />
                  )}
                  <Chip size="small" variant="outlined" label={path} />
                  {exploit?.cwe && <Chip size="small" variant="outlined" label={`CWE: ${exploit.cwe}`} />}
                  {exploit?.tool && <Chip size="small" variant="outlined" label={`Tool: ${exploit.tool}`} />}
                </Stack>

                {exploit?.description && (
                  <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>
                    {exploit.description}
                  </Typography>
                )}
              </Paper>

              {/* Command (curl) */}
              {exploit?.command && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Command
                    </Typography>
                    <Tooltip title="Copy command">
                      <IconButton size="small" onClick={() => copy(exploit.command)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <SyntaxHighlighter
                    language={commandLang}
                    style={prism}
                    customStyle={{ borderRadius: 8, margin: 0, fontSize: '0.9rem' }}
                    wrapLines
                  >
                    {String(exploit.command).trim()}
                  </SyntaxHighlighter>
                </Paper>
              )}

              {/* Command Output */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Command Output
                  </Typography>
                  <Tooltip title="Copy output">
                    <IconButton size="small" onClick={() => copy(out.text)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <SyntaxHighlighter
                  language={out.lang}
                  style={prism}
                  customStyle={{ borderRadius: 8, margin: 0, fontSize: '0.9rem' }}
                  wrapLines
                >
                  {out.text}
                </SyntaxHighlighter>
              </Paper>
            </Stack>
          )}
        </Box>
      </Modal>

      {/* Report Modal (kept, nicer look; POC removed) */}
      <Modal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        aria-labelledby="report-modal-title"
        aria-describedby="report-modal-desc"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '94%', md: '70%' },
            maxHeight: '80%',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2.5,
            p: { xs: 2, md: 3 },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography id="report-modal-title" variant="h6" sx={{ fontWeight: 800 }}>
              Report
            </Typography>
            <IconButton onClick={() => setReportOpen(false)} aria-label="close-report">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <ReactMarkdown
              components={{
                code({ className, children, ...props }) {
                  const isBlock = className && className.startsWith('language-');
                  if (!isBlock) return <code {...props}>{children}</code>;
                  const language = className?.replace('language-', '') || 'text';
                  return (
                    <SyntaxHighlighter
                      language={language}
                      style={prism}
                      wrapLines
                      customStyle={{ borderRadius: 8, margin: 0, fontSize: '0.9rem' }}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  );
                },
              }}
            >
              {exploit?.report_details || 'No report details available'}
            </ReactMarkdown>
          </Paper>
        </Box>
      </Modal>
    </>
  );
};

export default ExploitsModal;
